// How the web app learns it is running inside the native shell's WebView.
//
// Two independent signals, because they answer different questions:
//
//   1. The User-Agent suffix is set by the native WebView on EVERY request, so
//      server code can tell an app request from a browser one.
//   2. `?embed=native` → `rt_embed` cookie is the explicit, greppable client
//      signal, and the only one a desktop browser or Playwright can fake. The
//      cookie survives in-WebView navigation so the query need not be repeated.
//
// `isNativeHost()` on the client answers true for either.

export const NATIVE_UA_PRODUCT = 'RoomTreatmentNative' as const;

export type NativeHostPlatform = 'ios' | 'android';

export interface NativeHostInfo {
  platform: NativeHostPlatform;
  appVersion: string;
}

/** Builds the UA suffix the WebView appends: `RoomTreatmentNative/1.2.0 (ios)`. */
export function buildNativeUserAgentSuffix(info: NativeHostInfo): string {
  return `${NATIVE_UA_PRODUCT}/${info.appVersion} (${info.platform})`;
}

const UA_RE = new RegExp(`${NATIVE_UA_PRODUCT}/([^\\s]+) \\((ios|android)\\)`);

export function parseNativeHostUserAgent(userAgent: string | null | undefined): NativeHostInfo | null {
  if (!userAgent) return null;
  const m = UA_RE.exec(userAgent);
  if (!m) return null;
  return { appVersion: m[1], platform: m[2] as NativeHostPlatform };
}

export function isNativeHostUserAgent(userAgent: string | null | undefined): boolean {
  return parseNativeHostUserAgent(userAgent) !== null;
}

/** `?embed=native` on any URL opts a plain browser into the embedded layout. */
export const NATIVE_EMBED_QUERY_KEY = 'embed' as const;
export const NATIVE_EMBED_QUERY_VALUE = 'native' as const;
/** Cookie the web app sets once it has seen the query, so the flag persists. */
export const NATIVE_EMBED_COOKIE = 'rt_embed' as const;

export function hasNativeEmbedQuery(search: string | URLSearchParams | null | undefined): boolean {
  if (!search) return false;
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  return params.get(NATIVE_EMBED_QUERY_KEY) === NATIVE_EMBED_QUERY_VALUE;
}

export function hasNativeEmbedCookie(cookieHeader: string | null | undefined): boolean {
  if (!cookieHeader) return false;
  return cookieHeader
    .split(';')
    .some((part) => part.trim() === `${NATIVE_EMBED_COOKIE}=1`);
}

/**
 * Client-side: is this page inside the native shell? True when the WebView's
 * `postMessage` hook exists, or when the embed cookie/query is present (the
 * desktop/Playwright rehearsal path, where messages go nowhere but the layout
 * is the embedded one).
 */
export function isNativeHost(win: Pick<Window, 'navigator' | 'location'> & { document?: { cookie?: string }; ReactNativeWebView?: unknown }): boolean {
  if (win.ReactNativeWebView) return true;
  if (isNativeHostUserAgent(win.navigator?.userAgent)) return true;
  if (hasNativeEmbedQuery(win.location?.search)) return true;
  return hasNativeEmbedCookie(win.document?.cookie);
}

/** Can messages actually reach a native host from this window? */
export function hasNativeTransport(win: { ReactNativeWebView?: { postMessage?: unknown } }): boolean {
  return typeof win.ReactNativeWebView?.postMessage === 'function';
}
